import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Button from '../../components/Button';
import { useUserProfile } from '../../hooks/useUserProfile';
import TaskDetailModal from '../../components/TaskDetailModal';
import enhancedApiClient from '../../api/enhancedApiClient';

const VendorEventDetail = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const [selectedTask, setSelectedTask] = useState(null);
  const [showDetailView, setShowDetailView] = useState(false);
  const [event, setEvent] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [guests, setGuests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);
  const [venueName, setVenueName] = useState('');
  
  useEffect(() => {
    const getCurrentUser = async () => {
      try {
        const { Auth } = await import('aws-amplify');
        const user = await Auth.currentAuthenticatedUser();
        setCurrentUser(user);
      } catch (error) {
        console.log('No authenticated user');
      }
    };
    getCurrentUser();
  }, []);
  
  const { userProfile } = useUserProfile(currentUser?.username);
  const vendorId = userProfile?.currentVendorId;

  useEffect(() => {
    const loadEventData = async () => {
      if (!eventId) return;
      
      try {
        const [eventData, tasksData, guestsData] = await Promise.all([
          fetch(`https://etz8ecs10a.execute-api.ap-southeast-2.amazonaws.com/prod/api/events/${eventId}`).then(r => r.json()),
          enhancedApiClient.getTasks(eventId),
          fetch(`https://etz8ecs10a.execute-api.ap-southeast-2.amazonaws.com/prod/api/guests/${eventId}`).then(r => r.json())
        ]);
        
        setEvent(eventData);
        setTasks(tasksData || []);
        setGuests(guestsData || []);
        
        // Load venue name
        const vendors = await fetch(`https://etz8ecs10a.execute-api.ap-southeast-2.amazonaws.com/prod/api/events/${eventId}/vendors`).then(r => r.json());
        const venue = vendors.find(v => v.vendorType === 'venue');
        if (venue) setVenueName(venue.name);
        
        // Auto-select first incomplete task
        const incompleteTasks = tasksData.filter(t => t.status !== 'completed');
        if (incompleteTasks.length > 0) {
          setSelectedTask(incompleteTasks[0]);
        } else if (tasksData.length > 0) {
          setSelectedTask(tasksData[0]);
        }
      } catch (error) {
        console.error('Error loading event data:', error);
      } finally {
        setLoading(false);
      }
    };
    
    loadEventData();
  }, [eventId]);

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        flexDirection: 'column',
        alignItems: 'center', 
        justifyContent: 'center',
        minHeight: '60vh',
        gap: '16px'
      }}>
        <div style={{
          width: '40px',
          height: '40px',
          border: '3px solid #E5E7EB',
          borderTop: '3px solid #6B9080',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }}></div>
        <div style={{ color: '#6B7280', fontSize: '16px' }}>
          Loading event...
        </div>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="page-header" style={{
        backgroundColor: '#E8F4E8',
        padding: '48px 24px 200px 24px',
        textAlign: 'center',
        position: 'relative',
        marginBottom: '60px'
      }}>
        <div style={{ 
          position: 'absolute',
          top: '120px',
          left: '50%',
          transform: 'translateX(-50%)',
          fontSize: '16px', 
          color: '#6B7280', 
          fontWeight: '400'
        }}>
          {event?.eventDate ? new Date(event.eventDate).toLocaleDateString('en-US', { day: 'numeric', month: 'long', year: 'numeric' }) : ''} · {venueName}
        </div>
        <h1 className="page-title" style={{ 
          fontSize: '56px', 
          fontWeight: '400', 
          color: '#1F2937',
          margin: 0,
          fontFamily: 'Georgia, serif',
          letterSpacing: '-0.02em'
        }}>
          {event?.clientName || 'Event'}
        </h1>
        
        {/* Stats Cards */}
        <div className="stats-grid" style={{ 
          position: 'absolute',
          bottom: '60px',
          left: '50%',
          transform: 'translateX(-50%)',
          display: 'grid', 
          gridTemplateColumns: 'repeat(3, 1fr)', 
          gap: '20px',
          width: '100%',
          maxWidth: '1200px',
          padding: '0 24px'
        }}>
          <div style={{ 
            backgroundColor: 'white',
            padding: '20px',
            borderRadius: '12px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            minWidth: '150px'
          }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#6B9080" strokeWidth="1.5">
              <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
            </svg>
            <div>
              <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#1F2937' }}>
                Confirmed
              </div>
            </div>
          </div>

          <div style={{ 
            backgroundColor: 'white',
            padding: '20px',
            borderRadius: '12px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            minWidth: '150px'
          }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#6B9080" strokeWidth="1.5">
              <circle cx="12" cy="12" r="10"/>
              <path d="M12 6v6l4 2"/>
            </svg>
            <div>
              <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#1F2937', display: 'flex', alignItems: 'baseline', gap: '8px' }}>
                ${event?.budget ? (event.budget / 1000).toFixed(0) + 'k' : '0'} <span style={{ fontSize: '14px', color: '#000000', fontWeight: 'normal' }}>total value</span>
              </div>
            </div>
          </div>

          <div style={{ 
            backgroundColor: 'white',
            padding: '20px',
            borderRadius: '12px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            minWidth: '150px'
          }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#6B9080" strokeWidth="1.5">
              <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/>
              <circle cx="9" cy="7" r="4"/>
              <path d="M22 21v-2a4 4 0 0 0-3-3.87"/>
              <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
            </svg>
            <div>
              <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#1F2937', display: 'flex', alignItems: 'baseline', gap: '8px' }}>
                {guests.length} <span style={{ fontSize: '14px', color: '#000000', fontWeight: 'normal' }}>estimated guests</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container" style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 24px 24px 24px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '24px' }}>
          
          {/* Checklist Sidebar */}
          <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', height: 'fit-content' }}>
            <h3 style={{ fontSize: '20px', fontWeight: '600', color: '#1F2937', marginBottom: '20px', fontFamily: 'Georgia, serif' }}>
              Checklist
            </h3>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {tasks.slice(0, 5).map(task => (
                <div 
                  key={task.id}
                  onClick={() => setSelectedTask(task)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '12px',
                    backgroundColor: selectedTask?.id === task.id ? '#F0F9FF' : '#F9FAFB',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    border: selectedTask?.id === task.id ? '1px solid #6B9080' : '1px solid transparent'
                  }}
                >
                  <div style={{
                    width: '40px',
                    height: '40px',
                    backgroundColor: '#E8F4E8',
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    position: 'relative'
                  }}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#6B9080" strokeWidth="1.5">
                      <path d="M9 11l3 3L22 4"></path>
                      <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"></path>
                    </svg>
                    {task.status === 'completed' && (
                      <div style={{
                        position: 'absolute',
                        top: '-4px',
                        right: '-4px',
                        width: '16px',
                        height: '16px',
                        backgroundColor: '#10B981',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3">
                          <polyline points="20,6 9,17 4,12"/>
                        </svg>
                      </div>
                    )}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: '500', color: '#1F2937', fontSize: '14px' }}>{task.name}</div>
                    <div style={{ color: '#6B7280', fontSize: '12px' }}>{task.dueDate ? new Date(task.dueDate).toLocaleDateString('en-US', { day: 'numeric', month: 'short' }) : ''}</div>
                  </div>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#6B7280" strokeWidth="2">
                    <polyline points="9,18 15,12 9,6"/>
                  </svg>
                </div>
              ))}
            </div>
            
            <div style={{ marginTop: '20px', textAlign: 'center' }}>
              <button 
                onClick={() => navigate('/vendor/checklist')}
                style={{
                color: '#6B9080',
                backgroundColor: 'transparent',
                border: 'none',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                margin: '0 auto'
              }}>
                View or edit checklist
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M5 12h14M12 5l7 7-7 7"/>
                </svg>
              </button>
            </div>
          </div>

          {/* Task Detail */}
          {selectedTask && (
            <div style={{ backgroundColor: '#E8F4E8', borderRadius: '12px', padding: '32px' }}>
              <h2 style={{ fontSize: '32px', fontWeight: '600', color: '#1F2937', marginBottom: '8px', fontFamily: 'Georgia, serif' }}>
                {selectedTask.name}
              </h2>
              <div style={{ color: '#6B7280', fontSize: '16px', marginBottom: '24px' }}>
                {selectedTask.status === 'completed' ? 'Completed' : 'Awaiting client acceptance'}
              </div>
              
              <p style={{ color: '#4B5563', fontSize: '16px', lineHeight: '1.6', marginBottom: '32px' }}>
                {selectedTask.details || 'No additional details provided.'}
              </p>

              {/* Document */}
              {selectedTask.documentUrl && (
                <div style={{
                  backgroundColor: 'white',
                  borderRadius: '12px',
                  padding: '20px',
                  marginBottom: '24px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '16px'
                }}>
                  <div style={{
                    width: '48px',
                    height: '48px',
                    backgroundColor: '#6B9080',
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white'
                  }}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.5">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                      <polyline points="14,2 14,8 20,8"/>
                      <line x1="16" y1="13" x2="8" y2="13"/>
                      <line x1="16" y1="17" x2="8" y2="17"/>
                      <polyline points="10,9 9,9 8,9"/>
                    </svg>
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: '600', fontSize: '16px', color: '#1F2937', marginBottom: '4px' }}>
                      {selectedTask.documentUrl.split('/').pop().split('-').slice(1).join('-') || 'Document'}
                    </div>
                    <div style={{ color: '#6B7280', fontSize: '14px' }}>
                      Uploaded {selectedTask.createdAt ? new Date(selectedTask.createdAt).toLocaleDateString('en-US', { day: 'numeric', month: 'short' }) : ''}
                    </div>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div style={{ display: 'flex', gap: '12px' }}>
                <Button 
                  variant="primary"
                  onClick={() => {
                    setShowDetailView(true);
                  }}
                >
                  View Details
                </Button>
                <Button 
                  variant="secondary"
                  onClick={() => navigate('/vendor/messages')}
                >
                  Open Messages
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
      
      <TaskDetailModal
        task={showDetailView ? selectedTask : null}
        onClose={() => setShowDetailView(false)}
        eventId={eventId}
        currentUser={currentUser}
        onStatusUpdate={() => {
          // Reload event data
          window.location.reload();
        }}
      />
    </div>
  );
};

export default VendorEventDetail;